import { useEffect, useSyncExternalStore } from 'react';

import { API_ACCOUNT } from './EsConstants';
import { esRead } from './EsFetch';

/**
 * What the account circle in the nav draws: the account's uploaded avatar, else the Google picture, else its initials (esback decides which
 * and sends only one of avatarRev and pictureUrl).
 *
 * <p>ONE store, because the nav reads it and the Personal tab writes it -- an upload, a
 * delete, or an initials save repaints the circle without a reload. shownInitials comes
 * from esback, derived from the display name when none are set, so no fallback rule lives
 * here (EsAccountView says why).
 *
 * <p>ringColor is the player's color at the table on screen, set by the console while it is
 * mounted: it lives here, not on the account, because only a seat has one (chipColorOf). A
 * read of the account keeps it.
 */
const EMPTY = { loaded: false, displayName: null, shownInitials: null, avatarRev: null, pictureUrl: null, ringColor: null };

let gState = EMPTY;
let gInFlight = null;
const gWatchers = new Set();

const setState = ( pNext ) => {
    gState = pNext;
    gWatchers.forEach( ( pNotify ) => pNotify() );
};

const subscribe = ( pNotify ) => {
    gWatchers.add( pNotify );
    return () => gWatchers.delete( pNotify );
};

const snapshot = () => gState;

const faceOf = ( pView ) => ( {
    loaded: true,
    displayName: pView?.displayName ?? null,
    shownInitials: pView?.shownInitials ?? null,
    avatarRev: pView?.avatarRev ?? null,
    pictureUrl: pView?.pictureUrl ?? null,
    ringColor: gState.ringColor,
} );

/** Take the face from an EsAccountView the caller already holds. */
export function esAccountFaceSet( pView ) {
    setState( faceOf( pView ) );
}

/** An upload: the new version, and esback now sends no Google picture beside it. */
export function esAccountFaceRev( pAvatarRev ) {
    setState( { ...gState, avatarRev: pAvatarRev ?? null, pictureUrl: null } );
}

/** The console's player color, or null to go back to the spectrum. */
export function esAccountFaceRing( pColor ) {
    if ( ( pColor ?? null ) !== gState.ringColor ) setState( { ...gState, ringColor: pColor ?? null } );
}

export function esAccountFaceRefresh() {
    if ( gInFlight ) return gInFlight;

    gInFlight = esRead( API_ACCOUNT )
        .then( ( pView ) => setState( faceOf( pView ) ) )
        // A 404 is an authenticated session with no row; the circle then wears the empty face.
        .catch( () => setState( { ...EMPTY, loaded: true, ringColor: gState.ringColor } ) )
        .finally( () => { gInFlight = null; } );

    return gInFlight;
}

/** Tests only: forget what was read. */
export function esAccountFaceReset() {
    gInFlight = null;
    setState( EMPTY );
}

/** Reads nothing unless enabled, which is somebody logged in. */
export function useEsAccountFace( pEnabled ) {
    const aState = useSyncExternalStore( subscribe, snapshot, snapshot );

    useEffect( () => {
        if ( pEnabled && !aState.loaded ) esAccountFaceRefresh();
    }, [ pEnabled, aState.loaded ] );

    return aState;
}
